import { Backdrop, CircularProgress, TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Create } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import isEqual from "lodash/isEqual";
import getStyles from "./getStyles";
import CancelIcon from "@material-ui/icons/Cancel";
import "./DenominationsModalDialog.scss";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

type ValueAndUnits = {
  value: number;
  units: number;
};

export default function DenominationsModalDialog(props: any) {
  const [denomsInvalid, setDenomsInvalid] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [totalSelected, setTotalSelected] = React.useState<number>(0);
  const [denominationsMap, setDenominationsMap] = useState<{
    [index: string]: ValueAndUnits;
  }>({});

  useEffect(() => {
    if (props.value) {
      const newMap: { [index: number]: any } = {};
      for (let i = 0; i < props.value.length; i++) {
        const units = props.value[i].number;
        const sValue = speciesValue(props.value[i]);
        newMap[props.value[i].unitValue] = {
          value: units * (sValue || 0),
          units
        };
      }
      if (!isEqual(denominationsMap, newMap)) {
        setDenominationsMap(newMap);
      }
    }
  }, [props.value]);

  useEffect(() => {
    let total = 0;
    setTotalSelected(0);
    for (const key in denominationsMap) {
      total += (denominationsMap[key] && denominationsMap[key]["value"]) || 0;
    }
    setTotalSelected(total);
    const someAreInvalid =
      props.denominations &&
      props.denominations.filter((denomination: any) => {
        return !calculateUnits(denomination)[1];
      }).length > 0;
    setDenomsInvalid(someAreInvalid);
  });

  useEffect(() => {
    setDenominationsMap(([] as {}) as any);
  }, [props.currencyAmount]);

  const handleClickOpen = () => {
    props.onChange(
      Object.keys(denominationsMap).map(unitValue => ({
        unitValue: parseInt(unitValue),
        orderValue: getValue({ unitValue })
      }))
    );
    setOpen(props.openDenominationsModal);
  };

  const handleClose = () => {
    setOpen(false);
    if (props.onChange) {
      props.onChange(
        Object.keys(denominationsMap)
          .map(unitValue => {
            return {
              unitValue: parseInt(unitValue),
              orderValue: getValue({ unitValue })
            };
          })
          .filter(i => i.orderValue)
      );
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const classes = getStyles();

  function speciesValue(denomination: any): number | null {
    return denomination.unitValue;
  }

  function getValue(denomination: any): number {
    if (!denominationsMap[denomination.unitValue]) {
      return 0;
    }
    return denominationsMap[denomination.unitValue].value || 0;
  }

  function calculateUnits(denomination: any): [string, boolean] {
    if (!denominationsMap[denomination.unitValue]) {
      return ["-", true];
    }
    const value = denominationsMap[denomination.unitValue].value || 0;
    const specValue = speciesValue(denomination);
    if (specValue && value % specValue !== 0) {
      return ["-", false];
    }
    const calcUnits = specValue == null ? 0 : Math.round(value / specValue);
    if (calcUnits === 0) {
      return ["-", true];
    }
    return [`${calcUnits}`, true];
  }

  let totalRemaining = props.currencyAmount - totalSelected;
  return (
    <div className="DenominationsButton--mainContainer">
      <div className="DenominationsButton--container">
        <Button
          variant="contained"
          className={`DenominationsButton-button ${
            props.error
              ? classes.denominationsButtonError
              : classes.denominationsButton
          }`}
          onClick={handleClickOpen}
          data-testid="Denominations-openButton"
        >
          <Create />
        </Button>

        {props.clickedActionButton && !props.error ? (
          <CheckCircleIcon className="InputField--checkIcon" />
        ) : props.error ? (
          <CancelIcon className="InputField--cancelIcon" />
        ) : (
          <></>
        )}

        {!props.isLoadingDenominations ? (
          <Dialog
            className="DenominationsDialog-dialog"
            open={open}
            fullWidth={true}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle
              className={classes.titleBar}
              id="denominations-dialog-title"
            >
              {"Edit Required Denominations"}
            </DialogTitle>
            <DialogContent className={classes.denominationsContainer}>
              <div className={classes.tableHeaders}>
                <p className={classes.tableHeader}>Denomination</p>
                <p className={classes.tableHeader}>
                  Value {props.currency && props.currency.name}
                </p>
                <p className={classes.tableHeader}>Units</p>
              </div>
              {props.denominations &&
                props.denominations.map((denomination: any, i: number) => {
                  const [units, valid] = calculateUnits(denomination);
                  return (
                    <div
                      key={i}
                      className={`${classes.inputContainer} DenominationsDialog-dialogInput`}
                    >
                      <p
                        className={`DenominationsModalDialog-denominationName ${classes.input}`}
                      >
                        {denomination.unitValue &&
                          `${denomination.unitValue.toLocaleString(
                            navigator.language,
                            {
                              minimumFractionDigits: 0
                            }
                          )}s`}
                      </p>
                      <form noValidate autoComplete="off">
                        <TextField
                          disabled={
                            denomination.unitValue > props.currencyAmount
                          }
                          className={`${classes.inputWidth} DenominationsDialog-value`}
                          type="number"
                          onFocus={e => e.target.select()}
                          data-testid={`value-${denomination.unitValue}`}
                          inputProps={{
                            defaultValue: "0",
                            step: denomination.unitValue
                          }}
                          error={!valid}
                          key={i}
                          value={
                            denominationsMap[denomination.unitValue] &&
                            denominationsMap[denomination.unitValue].value
                          }
                          onChange={evt => {
                            if (evt && evt.target) {
                              const value = evt.target.value;
                              setDenominationsMap(map => {
                                const newMap = { ...map };
                                newMap[denomination.unitValue] = {
                                  value: Math.abs(parseInt(value)),
                                  units: Math.round(parseInt(units))
                                };
                                return newMap;
                              });
                            }
                          }}
                        />
                      </form>
                      <p
                        data-testid={`units-${denomination.unitValue}`}
                        className={`${classes.input} DenominationsDialog-units`}
                      >
                        {valid ? units : "-"}
                      </p>
                    </div>
                  );
                })}

              <div className={classes.summaryContainer}>
                <div className={classes.summaryItemContainer}>
                  <div className={classes.summaryTitle}>Fulfilment Value:</div>
                  <div className={classes.summaryValue}>
                    {props.currency && props.currency.symbol}
                    {props.currencyAmount}
                  </div>
                </div>

                <div className={classes.summaryItemContainer}>
                  <div className={classes.summaryTitle}>Selected Value:</div>
                  <div
                    className={`${classes.summaryValue} DenominationsDialog-total`}
                  >
                    {props.currency && props.currency.symbol}
                    {totalSelected}
                  </div>
                </div>

                <div className={classes.summaryItemContainer}>
                  <div className={classes.summaryTitle}>
                    Remaining Fulfilment Value:
                  </div>
                  <div
                    className={`${classes.summaryValue} DenominationsDialog-remaining`}
                  >
                    {props.currency && props.currency.symbol}
                    {totalRemaining >= 0
                      ? totalRemaining
                      : `${Math.abs(totalRemaining)} over`}
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                className="DenominationsDialog-cancelButton"
                onClick={handleCancel}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                className="DenominationsDialog-okButton"
                onClick={handleClose}
                color="primary"
                autoFocus
                disabled={totalRemaining !== 0 || denomsInvalid}
                data-testid="DenominationsDialog-okButton"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        ) : (
          <div>
            <Backdrop className={classes.backdrop} open={true}>
              <CircularProgress color="inherit" />
            </Backdrop>
          </div>
        )}
      </div>
      {props.error ? (
        <p className="MuiFormHelperText-root DenominationsButton--errorMsg Mui-error">
          {props.error}
        </p>
      ) : (
        <></>
      )}
    </div>
  );
}
